<script lang="jsx">
  /* eslint-disable @typescript-eslint/no-empty-function */
  import { map, pick, debounce } from 'lodash'
  import Sortable from 'sortablejs'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import initStyle, { hideScrollBar } from './style'
  import TableHead from './TableHead.vue'
  import TableBody from './TableBody.vue'

  initStyle()

  function renderEmpty(data, placeholder) {
    if (data && data.length > 0) {
      return null
    }
    if (!placeholder) {
      return null
    }
    return (
      <NewBsBox class="empty-list" bs={{ 'cmp.table': 'empty' }}>
        {placeholder}
      </NewBsBox>
    )
  }

  const TablePart = {
    props: ['bs'],
    render() {
      return (
        <NewBsBox
          as="table"
          bs={{
            'cmp.table': 'layout',
            ...this.bs,
          }}
          border="0"
          cellpadding="0"
          cellspacing="0"
        >
          {this.$slots.default()}
        </NewBsBox>
      )
    },
  }

  const BaseTable = {
    name: 'BTable',
    data() {
      return {
        oTheadTable: null,
        oTbodyTable: null,
        oFixedLeftTable: null,
        oFixedRightTable: null,
        screenWidth: 0,
        tbodyHeights: [],
        theadHeight: 0,
        onFixedLeft: false,
        onFixedRight: false,
        mouseenterActiveIndex: -1,
        oDrag: null,
      }
    },
    props: {
      layoutBs: {
        type: Object,
        default: () => ({}),
      },
      layoutTheadBs: {
        type: Object,
        default: () => ({}),
      },
      layoutTbodyBs: {
        type: Object,
        default: () => ({}),
      },
      columns: {
        type: Array,
        default: () => ([]),
        required: true,
      },
      data: {
        type: Array,
        default: () => ([]),
        required: true,
      },
      currentSelect: {
        type: Number,
        default: -1,
      },
      height: {
        type: [Number, String],
      },
      /**
       * 当有列fixed的时候，table横向滚动区域的width
       */
      scrollX: {
        type: Number,
        default: 0,
      },
      placeholder: {
        type: [String, Object],
      },
      striped: {
        type: Boolean,
        default: false,
      },
      /**
       * 支持状态值 success、danger、warning、lowlight, 颜色编码，bs object
       */
      rowStatus: {
        type: Function,
        default: () => (() => {}),
      },
      idKey: {
        type: String,
      },
      spanMethod: {
        type: Function,
        default: () => (() => {}),
      },
      borderStyle: {
        type: String,
        default: 'full',
      },
      align: {
        type: String,
        default: 'left',
      },
      size: {
        type: String,
        default: 'm',
      },
      dragOption: {
        type: Object,
        default: () => ({
          enable: false,
        }),
      },
      syncFixedRowHover: {
        type: Boolean,
        default: true,
      },
    },
    computed: {
      filterColumns() {
        return this.columns.map(column => {
          column.th = column.th || {}
          column.td = column.td || {}
          if (column.fixed) {
            column.fixed = column.fixed !== 'right' ? 'left' : 'right'
          }
          return column
        })
      },
      fixedLeftColums() {
        return this.filterColumns.filter(column => column.fixed === 'left')
      },
      fixedLeftData() {
        const pickedFiled = map(this.fixedLeftColums, 'key')
        return map(this.data, item => pick(item, pickedFiled))
      },
      fixedRightColums() {
        return this.filterColumns.filter(column => column.fixed === 'right')
      },
      fixedRightData() {
        const pickedFiled = map(this.fixedRightColums, 'key')
        return map(this.data, item => pick(item, pickedFiled))
      },
      hasFixedLeft() {
        return this.fixedLeftColums.length > 0
      },
      hasFixedRight() {
        return this.fixedRightColums.length > 0
      },
      shouldFixedColumn() {
        return this.hasFixedLeft || this.hasFixedRight
      },
      shouldFixedHead() {
        return Boolean(this.height)
      },
    },
    emits: ['row-click'],
    methods: {
      initDrag() {
        const oTbody = this.$refs.tbody.$el
        this.oDrag = Sortable.create(oTbody, {
          filter: '.cell-wrap',
          preventOnFilter: false,
          ...this.dragOption,
        })
      },
      handleRowMouseenter(rowIndex) {
        this.mouseenterActiveIndex = rowIndex
      },
      handleRowMouseleave() {
        this.mouseenterActiveIndex = -1
      },
      handleRowClick({
        rowData,
        rowIndex,
      }) {
        this.$emit('row-click', {
          rowData,
          rowIndex,
        })
      },
      tbodyTrHeightChange({ rowIndex, height }) {
        this.tbodyHeights[rowIndex] = height
        this.tbodyHeights = [
          ...this.tbodyHeights,
        ]
      },
      theadTrHeightChange(height) {
        this.theadHeight = height
      },
      bodyFollow() {
        this.oTbodyTable.scrollLeft = this.oTheadTable.scrollLeft
      },
      headFollow() {
        this.oTheadTable.scrollLeft = this.oTbodyTable.scrollLeft
      },
      fixedTableHorizontalFollow(curScrollTableName) {
        return () => {
          const arr = ['oTbodyTable', 'oTheadTable']
          arr.forEach(tableName => {
            if (tableName !== curScrollTableName) {
              this[tableName].$el.scrollLeft = this[curScrollTableName].$el.scrollLeft
            }
          })
        }
      },
      fixedTableVerticalFollow(curScrollTableName) {
        return () => {
          const arr = ['oFixedLeftTable', 'oFixedRightTable', 'oTbodyTable']
          arr.forEach(tableName => {
            if (tableName !== curScrollTableName) {
              if (this[tableName]) {
                this[tableName].$el.scrollTop = this[curScrollTableName].$el.scrollTop
              }
            }
          })
        }
      },
    },
    mounted() {
      if (this.dragOption.enable === true) {
        this.initDrag()
      }
      const passiveOption = { passive: true }
      this.oTheadTable = this.$refs.tableHead
      this.oTbodyTable = this.$refs.tableBody
      this.oFixedLeftTable = this.$refs.fixedLeftTable
      this.oFixedRightTable = this.$refs.fixedRightTable
      if (this.oTheadTable) {
        this.oTheadTable.$el.addEventListener('scroll', this.fixedTableHorizontalFollow('oTheadTable'), passiveOption)
        this.oTbodyTable.$el.addEventListener('scroll', this.fixedTableHorizontalFollow('oTbodyTable'), passiveOption)
        this.oTbodyTable.$el.addEventListener('scroll', this.fixedTableVerticalFollow('oTbodyTable'), passiveOption)
      }
      if (this.oFixedLeftTable) {
        this.oFixedLeftTable.$el.addEventListener('scroll', this.fixedTableVerticalFollow('oFixedLeftTable'), passiveOption)
      }
      if (this.oFixedRightTable) {
        this.oFixedRightTable.$el.addEventListener('scroll', this.fixedTableVerticalFollow('oFixedRightTable'), passiveOption)
      }
      if (this.oTbodyTable && this.shouldFixedColumn) {
        const onFixedLeftFn = oScrollDiv => {
          const scrollLeft = oScrollDiv.scrollLeft
          this.onFixedLeft = scrollLeft <= 0
        }
        const onFixedRightFn = oScrollDiv => {
          const scrollLeft = oScrollDiv.scrollLeft
          const wrapWidth = oScrollDiv.offsetWidth
          const sumWidth = oScrollDiv.scrollWidth
          this.onFixedRight = scrollLeft >= 0 && wrapWidth + scrollLeft >= sumWidth
        }
        const onCreateFitFixedStyle = () => {
          onFixedLeftFn(this.oTbodyTable)
          onFixedRightFn(this.oTbodyTable)
        }
        onCreateFitFixedStyle()
        this.oTbodyTable.$el.addEventListener('scroll', e => onFixedLeftFn(e.target), passiveOption)
        this.oTbodyTable.$el.addEventListener('scroll', e => onFixedRightFn(e.target), passiveOption)
        window.addEventListener('resize', debounce(onCreateFitFixedStyle, 300))
      }
      this.screenWidth = document.documentElement.clientWidth
    },
    beforeUnmount() {
      if (this.oTheadTable) {
        this.oTheadTable.$el.removeEventListener('scroll', this.fixedTableHorizontalFollow('oTheadTable'))
        this.oTbodyTable.$el.removeEventListener('scroll', this.fixedTableHorizontalFollow('oTbodyTable'))
        this.oTbodyTable.$el.removeEventListener('scroll', this.fixedTableVerticalFollow('oTbodyTable'))
      }
      if (this.oFixedLeftTable) {
        this.oFixedLeftTable.$el.removeEventListener('scroll', this.fixedTableVerticalFollow('oFixedLeftTable'))
      }
      if (this.oFixedRightTable) {
        this.oFixedRightTable.$el.removeEventListener('scroll', this.fixedTableVerticalFollow('oFixedRightTable'))
      }
      if (this.oDrag) {
        this.oDrag.destroy()
      }
    },
    components: {
      TableHead,
      TableBody,
      NewBsBox,
    },
    render() {
      const {
        tableHead,
        tableTbody,
        footer,
        placeholder: placeholderSlot,
        ...scopedSlots
      } = this.$slots
      const slotsThead = tableHead ? tableHead() : null
      const slotsTbody = tableTbody ? tableTbody() : null
      const slotsFooter = footer ? footer() : null
      const slotsPlaceholder = placeholderSlot ? placeholderSlot() : null

      const {
        idKey,
        size,
        align,
        height,
        striped,
        borderStyle,
        spanMethod,
        currentSelect,
        data,
        shouldFixedColumn,
        shouldFixedHead,
        hasFixedLeft,
        hasFixedRight,
        onFixedLeft,
        onFixedRight,
        filterColumns,
        fixedLeftColums,
        fixedRightColums,
        fixedLeftData,
        fixedRightData,
        layoutBs,
        layoutTheadBs,
        layoutTbodyBs,
        handleRowClick,
        handleRowMouseenter,
        handleRowMouseleave,
        tbodyTrHeightChange,
        theadTrHeightChange,
        rowStatus,
        theadHeight,
        tbodyHeights,
        mouseenterActiveIndex,
        syncFixedRowHover,
      } = this

      const placeholder = slotsPlaceholder || this.placeholder

      const columnFixedStyle = shouldFixedColumn ? {
        width: `${this.scrollX || this.screenWidth}px`,
      } : {}
      const renderLayoutBs = this.shouldFixedHead ? layoutBs : {} // fit ResponsiveTable

      const renderThead = ({
        renderColumns,
        fixed,
        bs,
        shouldClacHeight = false,
        height, // eslint-disable-line
      }) => slotsThead || (
        <TableHead
          columns={renderColumns}
          bs={bs}
          borderStyle={borderStyle}
          propScopedSlots={scopedSlots}
          defaultSize={size}
          defaultAlign={align}
          fixed={fixed}
          height={height}
          shouldClacHeight={shouldClacHeight}
          theadTrHeightChange={theadTrHeightChange}
        />
      )

      const renderTbody = ({
        renderData,
        allData = [],
        renderColumns,
        bs,
        fixed,
        shouldClacHeight = false,
        heights = [],
      }) => slotsTbody || (
        <TableBody
          data={renderData}
          allData={allData}
          columns={renderColumns}
          bs={bs}
          syncFixedRowHover={syncFixedRowHover}
          idKey={idKey}
          fixed={fixed}
          striped={striped}
          shouldClacHeight={shouldClacHeight}
          rowStatus={rowStatus}
          shouldFixedColumn={shouldFixedColumn}
          currentSelect={currentSelect}
          spanMethod={spanMethod}
          propScopedSlots={scopedSlots}
          borderStyle={borderStyle}
          defaultSize={size}
          defaultAlign={align}
          handleRowClick={handleRowClick}
          tbodyTrHeightChange={tbodyTrHeightChange}
          heights={heights}
          mouseenterActiveIndex={mouseenterActiveIndex}
          handleRowMouseenter={handleRowMouseenter}
          handleRowMouseleave={handleRowMouseleave}
          ref="tbody"
        />
      )

      /*
        head 需要固定的table 需要分为上下两个table position sticky 有些bug
        <div>
          <div><table><tr><th></th></tr></table></div> // top table
          <div><table><tr><td></td></tr></table></div> // bottom table
        </div>
      */
      const renderHeadFixedTable = () => (
        <div>
          <NewBsBox
            bs={{
              overflowX: 'auto',
              ...hideScrollBar,
            }}
            ref="tableHead"
          >
            <TablePart
              bs={{
                ...layoutTheadBs,
                ...columnFixedStyle,
              }}
            >
              {renderThead({
                renderColumns: filterColumns,
                shouldClacHeight: shouldFixedColumn,
              })}
            </TablePart>
          </NewBsBox>
          <NewBsBox
            style="overflow-y: overlay;"
            bs={{
              'cmp.table': 'bodyWrap',
              height,
            }}
            ref="tableBody"
          >
            <TablePart
              bs={{
                ...layoutTbodyBs,
                ...columnFixedStyle,
              }}
            >
              {renderTbody({
                renderData: data,
                renderColumns: filterColumns,
                shouldClacHeight: shouldFixedColumn,
              })}
            </TablePart>
          </NewBsBox>
        </div>
      )

      /*
        head 不需要固定table
        <table>
          <thead><tr><th></th></tr></thead>
          <tbody><tr><td></td></tr></tbody>
        </table>
      */
      const renderDefaultTable = () => (
        <NewBsBox
          bs={{
            overflowX: 'auto',
            ...layoutBs,
          }}
          ref="tableBody"
        >
          <TablePart
            bs={{
              tableLayout: !hasFixedLeft && !hasFixedRight ? 'inherit' : 'fixed',
              ...columnFixedStyle,
            }}
          >
            {renderThead({
                renderColumns: filterColumns,
                bs: layoutTheadBs,
                shouldClacHeight: shouldFixedColumn,
              })}
            {renderTbody({
                renderData: data,
                renderColumns: filterColumns,
                bs: layoutTbodyBs,
                shouldClacHeight: shouldFixedColumn,
              })}
          </TablePart>
        </NewBsBox>
      )

      // column fixed table的结构 同理 default table 和 head fixed table
      const renderColumnFixedTable = (direction = 'left') => {
        const isLeft = direction === 'left'
        const shouldColumnFix = isLeft ? hasFixedLeft : hasFixedRight
        const fixedColumns = isLeft ? fixedLeftColums : fixedRightColums
        const fixedData = isLeft ? fixedLeftData : fixedRightData
        const fixedStyle = isLeft ? 'fixedLeftLayout' : 'fixedRightLayout'
        const fixedRef = isLeft ? 'fixedLeftTable' : 'fixedRightTable'

        if (!shouldColumnFix) {
          return null
        }
        return (
          <div>
            { /* top and column fixed table */ }
            {shouldFixedHead && (
              <NewBsBox
                bs={{
                  'cmp.table': fixedStyle,
                  'cmp.table.noShadow': isLeft ? onFixedLeft : onFixedRight,
                }}
              >
                <TablePart
                  bs={{
                    width: 'auto',
                    minWidth: 'auto',
                    ...layoutTheadBs,
                  }}
                >
                  {renderThead({
                    fixed: true,
                    renderColumns: fixedColumns,
                    height: theadHeight,
                  })}
                </TablePart>
                <NewBsBox
                style="overflow-y: overlay;"
                  bs={{
                    'cmp.table': 'bodyWrap',
                    height,
                    ...hideScrollBar,
                  }}
                  ref={fixedRef}
                >
                  <TablePart
                    bs={{
                      width: 'auto',
                      minWidth: 'auto',
                      ...layoutTbodyBs,
                    }}
                  >
                    {renderTbody({
                      fixed: true,
                      renderData: fixedData,
                      allData: data,
                      renderColumns: fixedColumns,
                      heights: tbodyHeights,
                    })}
                  </TablePart>
                </NewBsBox>
              </NewBsBox>
            )}

            { /* !top and column fixed table */ }
            {!shouldFixedHead && (
              <TablePart
                bs={{
                  width: 'auto',
                  minWidth: 'auto',
                  'cmp.table': fixedStyle,
                  'cmp.table.noShadow': isLeft ? onFixedLeft : onFixedRight,
                }}
              >
                {renderThead({
                  renderColumns: fixedColumns,
                  fixed: true,
                  bs: layoutTheadBs,
                  height: theadHeight,
                })}
                {renderTbody({
                  renderColumns: fixedColumns,
                  renderData: fixedData,
                  allData: data,
                  bs: layoutTbodyBs,
                  fixed: true,
                  heights: tbodyHeights,
                })}
              </TablePart>
            )}
          </div>
        )
      }

      return (
        <NewBsBox
          bs={{
            position: 'relative',
            ...renderLayoutBs,
          }}
        >
          {shouldFixedHead ? renderHeadFixedTable() : renderDefaultTable()}
          {renderColumnFixedTable('left')}
          {renderColumnFixedTable('right')}
          {renderEmpty(data, placeholder)}
          { slotsFooter }
        </NewBsBox>
      )
    },
  }

  export default BaseTable
</script>
